import { Divider, Drawer, Space } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import 'echarts-liquidfill';
import { CloseOutlined, DoubleRightOutlined } from '@ant-design/icons';
import { Button, EllipsisSpan, Paging, Table, Tag, Tooltip, useSearchParams, useTheme } from '@maxtropy/components';
import {
  apiV2PowerRoomConfigOperationInfoPagePost,
  apiV2PowerRoomConfigOperationInfoPost,
  V2PowerRoomConfigOperationInfoPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import {
  AlarmLevel,
  AlarmLevelColorDisplay,
  AlarmLevelDisplay,
  AlarmState,
  AlarmStateColorDisplay,
  AlarmStateDisplay,
} from '@/shared/types';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import qs from 'qs';

interface MaintenanceDrawerProps {
  roomId?: number;
  show: boolean;
  close: () => void;
}

const columns = [
  {
    title: '回路名称',
    fixed: 'left' as 'left',
    width: 220,
    dataIndex: 'circuitName',
    render: (v: string) => (
      <Tooltip title={v}>
        <div className={styles.content}>{v}</div>
      </Tooltip>
    ),
  },
  {
    title: '报警信息',
    dataIndex: 'alarmInfo',
    width: 220,
    ellipsis: { showTitle: true },
    render: (v: string) => (
      <Tooltip title={v}>
        <div className={styles.content}>{v}</div>
      </Tooltip>
    ),
  },
  {
    title: '状态',
    dataIndex: 'alarmStatus',
    ellipsis: { showTitle: true },
    width: 120,
    render: (v: AlarmState) => (
      <EllipsisSpan
        value={
          <Tag border="solid" color={AlarmStateColorDisplay[v]}>
            {AlarmStateDisplay[v]}
          </Tag>
        }
      />
    ),
  },
  {
    title: '报警等级',
    dataIndex: 'alarmLevel',
    ellipsis: { showTitle: true },
    width: 120,
    render: (v: AlarmLevel) => (
      <EllipsisSpan
        value={
          <Tag border="solid" color={AlarmLevelColorDisplay[v]}>
            {AlarmLevelDisplay[v]}
          </Tag>
        }
      />
    ),
  },
  {
    title: '报警时间',
    dataIndex: 'alarmTime',
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={v ? dayjs(v).format('YYYY/MM/DD HH:mm:ss') : '--'} />,
  },
  {
    title: '恢复时间',
    dataIndex: 'recoveryTime',
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={v ? dayjs(v).format('YYYY/MM/DD HH:mm:ss') : '--'} />,
  },
];

const MaintenanceDrawer: React.FC<MaintenanceDrawerProps> = ({ roomId, show, close }) => {
  const theme = useTheme();
  const [visible, setVisible] = useState<boolean>(show);
  const pagingInfo = useSearchParams(20);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;

  const [alarmInfo, setAlarmInfo] = useState<V2PowerRoomConfigOperationInfoPostResponse>();

  useEffect(() => {
    if (isNil(roomId)) return;
    apiV2PowerRoomConfigOperationInfoPost({ id: roomId }).then(res => {
      setAlarmInfo(res);
    });
  }, [roomId]);

  const { data, loading } = useRequest(
    () =>
      apiV2PowerRoomConfigOperationInfoPagePost({
        roomId: roomId,
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res.total ?? 0);
        return res.list ?? [];
      }),
    {
      ready: !isNil(roomId),
      refreshDeps: [roomId, pageOffset, pageSize],
    }
  );

  const circuitNames = useMemo(() => {
    return (alarmInfo?.rankList ?? []).map(m => m.circuitName).join('：') || '--';
  }, [alarmInfo]);

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      dataIndex: 'operation',
      fixed: 'right' as 'right',
      width: 140,
      render: (value: undefined, record: any) => {
        return (
          <Space size={16}>
            <Button
              type="link"
              onClick={() => {
                window.open(`/device/alarm/record/detail/${record.alarmId}`, '_blank');
              }}
            >
              详情
            </Button>
            <Button
              type="link"
              onClick={() => {
                window.open(
                  `${window.ALARMWORKORDER}/workOrder/repairWorkOrder/workOrderList/workOrderAdd?${qs.stringify(
                    {
                      problemSource: 10,
                      operationUnitCode: record.ouId,
                      alarmId: record.alarmId,
                      alarmTypeCode: -1,
                    },
                    { indices: false }
                  )}`,
                  '_blank'
                );
              }}
            >
              发起工单
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <Drawer
      title="运维信息"
      open={visible}
      width={800}
      className={styles.maintenanceDrawer}
      mask={false}
      getContainer={false}
      forceRender={true}
    >
      <div className={styles.titleWrapper}>
        <div className={styles.titleTxt}>运维信息</div>
        <CloseOutlined onClick={() => close()} size={16} style={{ color: 'var(--mx-text-desc-color)' }} />
      </div>
      <Divider />
      <div className={styles.contentWrapper}>
        <div className={styles.topContent}>
          <div style={{ width: 160 }}>
            <div className={styles.title}>当前未恢复报警</div>
            <div className={styles.val}>{alarmInfo?.notRecoverNum ?? '--'}</div>
          </div>
          <Divider type="vertical" style={{ height: 45 }} />
          <div style={{ width: 160, paddingLeft: 8 }}>
            <div className={styles.title}>近7日报警数据</div>
            <div>
              <Tooltip title="最高级">
                <span className={styles.top}>{alarmInfo?.topAlarmNum ?? 0}</span>
              </Tooltip>
              /
              <Tooltip title="高级">
                <span className={styles.high}>{alarmInfo?.highAlarmNum ?? 0}</span>
              </Tooltip>
              /
              <Tooltip title="中级">
                <span className={styles.middle}>{alarmInfo?.middleAlarmNum ?? 0}</span>
              </Tooltip>
            </div>
          </div>
          <Divider type="vertical" style={{ height: 45 }} />
          <div style={{ flex: 1, paddingLeft: 8 }}>
            <div className={styles.title}>高频报警回路</div>
            <Tooltip title={circuitNames}>
              <div className={styles.name}>{circuitNames}</div>
            </Tooltip>
          </div>
          <Divider type="vertical" style={{ height: 45 }} />
          <Button type="link" onClick={() => window.open(`${window.DMEPLATFORMSORIGIN}/dmes/alarm/record`, '_blank')}>
            报警列表
            <DoubleRightOutlined />
          </Button>
        </div>
        <Table loading={loading} rowKey="id" scroll={{ x: 1200 }} columns={buildColumns} dataSource={data} />
        <Paging pagingInfo={pagingInfo} />
      </div>
    </Drawer>
  );
};

export default MaintenanceDrawer;
